import React, { useState, useRef, useEffect } from "react";
import Icon from "../AppIcon";
import { useNavigation } from "./NavigationStateManager";
import { Modal, Button } from "react-bootstrap";

const StoreSelector = ({
  variant = "full",
  onStoreSelect,
  className = "",
  openModal,
  setOpenModal,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const {
    currentStore,
    anonymousMode,
    handleStoreSelection,
    getDisplayStoreName,
    stores,
    getAnonymousName,
  } = useNavigation();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current && variant === "full") {
      searchInputRef.current.focus();
    }
  }, [isOpen, variant]);

  useEffect(() => {
    if (openModal) {
      setSearchTerm("");
    }
  }, [openModal]);

  const filteredStores = stores
    .filter((store) => store.sBrandName)
    .filter((store) =>
      store.sBrandName.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const handleStoreClick = (store) => {
    handleStoreSelection(store);
    if (onStoreSelect) {
      onStoreSelect(store);
    }
    setIsOpen(false);
    setSearchTerm("");
    if (setOpenModal) {
      setOpenModal(false);
    }
  };

  if (variant === "compact") {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-text-primary bg-secondary-50 hover:bg-secondary-100 rounded-md transition-smooth focus-ring"
        >
          <Icon name="Building2" size={16} />
          <span className="hidden sm:block">
            {currentStore ? getDisplayStoreName() : "Select Store"}
          </span>
          <Icon name={isOpen ? "ChevronUp" : "ChevronDown"} size={16} />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-80 bg-surface rounded-lg shadow-lg border border-border animate-fade-in z-1010">
            <div className="p-4">
              <div className="text-sm font-medium text-text-primary mb-3">
                Select Testing Store
              </div>

              <div className="space-y-2">
                {filteredStores.map((store) => {
                  return (
                    <button
                      key={store.iTenantId}
                      onClick={() => handleStoreClick(store)}
                      className={`w-full text-left p-3 rounded-md transition-smooth border ${
                        currentStore?.iTenantId === store.iTenantId
                          ? "border-primary bg-primary-50"
                          : "border-border hover:border-secondary-300 hover:bg-secondary-50"
                      } `}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-text-primary">
                              {anonymousMode
                                ? getAnonymousName(store.iTenantId)
                                : store.sBrandName}
                              <span className="ml-2 px-2 py-1 text-xs rounded-full bg-success-100 text-success-700">
                                Active
                              </span>
                            </span>
                          </div>
                        </div>
                        {currentStore?.iTenantId === store.iTenantId && (
                          <Icon
                            name="Check"
                            size={16}
                            className="text-primary"
                          />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <Modal
      show={openModal}
      onHide={() => setOpenModal(false)}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Select Store</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="p-2">
          {/* Search Input */}
          <div className="mb-4">
            <div className="relative">
              <Icon
                name="Search"
                size={16}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400"
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search stores or categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-border rounded-md focus-ring focus:border-primary"
                autoFocus
              />
            </div>
          </div>

          {/* Store List */}
          <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
            {filteredStores.map((store) => {
              return (
                <button
                  key={store.iTenantId}
                  onClick={() => handleStoreClick(store)}
                  className={`w-full text-left p-4 rounded-lg transition-smooth border ${
                    currentStore?.iTenantId === store.iTenantId
                      ? "border-primary bg-primary-50"
                      : "border-border hover:border-secondary-300 hover:bg-secondary-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="font-medium text-text-primary">
                          {anonymousMode
                            ? getAnonymousName(store.iTenantId)
                            : store.sBrandName}
                          <span className="ml-2 px-2 py-1 text-xs rounded-full bg-success-100 text-success-700">
                            Active
                          </span>
                        </span>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-text-secondary">
                          Best for {store?.productTypes?.join(", ")}
                        </span>
                      </div>
                    </div>
                    {currentStore?.iTenantId === store.iTenantId && (
                      <Icon name="Check" size={20} className="text-primary" />
                    )}
                  </div>
                </button>
              );
            })}

            {filteredStores.length === 0 && (
              <div className="text-center py-8 text-text-secondary">
                <Icon
                  name="Search"
                  size={24}
                  className="mx-auto mb-2 opacity-50"
                />
                <p>No stores found matching your search.</p>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default StoreSelector;
